import { isEmpty, noop } from "lodash";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { reactQueryPost } from "@/react/helpers/backendRequestHelpers";
import { useRouterQueryParams } from "@/react/hooks/useRouterQueryParams";
import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";
import { AlreadyHaveAccountLink } from "@circle-react/components/OnboardingComponents/AlreadyHaveAccountLink";
import { SignupLink } from "@circle-react/components/OnboardingComponents/SignupLink";
import { EMAIL_REGEX } from "@circle-react/components/OnboardingPage/helpers/constants";
import { authenticationAppRoutes } from "@circle-react/components/OnboardingPage/helpers/routes";
import { OnboardingWrapper } from "@circle-react/components/OnboardingPage/layouts/OnboardingWrapper";
import { PageTitle } from "@circle-react/components/OnboardingPage/layouts/PageTitle";
import { Button } from "@circle-react-uikit/Button";
import { Form } from "@circle-react-uikit/Form";
import { useToast } from "@circle-react-uikit/ToastV2";
import { useIsNewOnboardingFlow } from "../hooks/useIsNewOnboardingFlow";
import { useOnboardingWhiteLabel } from "../hooks/useOnboardingWhiteLabel";

interface FormData {
  user: {
    email: string;
  };
}

interface ResetPasswordResponse {
  message: string;
  redirect_url?: string;
}

export const ForgotPasswordPage = () => {
  const localeScope = "authentication_app.forgot_password";
  const { currentCommunity } = useCurrentCommunityContext();
  const { success } = useToast();
  const { shouldShowPoweredByCircle } =
    useOnboardingWhiteLabel(currentCommunity);
  const { onboardingButtonVariant } = useIsNewOnboardingFlow();
  const urlParams = useRouterQueryParams();
  const history = useHistory();

  const defaultFormState: FormData = {
    user: {
      email: typeof urlParams.email === "string" ? urlParams.email : "",
    },
  };

  const { mutateAsync: handleSubmit, isLoading } = useMutation<
    ResetPasswordResponse,
    unknown,
    FormData
  >(
    (data: FormData) =>
      reactQueryPost(authenticationAppRoutes.resetPassword(), data),
    {
      onSuccess: (data: ResetPasswordResponse) => {
        success(data.message);
        if (data.redirect_url) {
          setTimeout(() => {
            history.push(authenticationAppRoutes.signIn());
          }, 1000);
        }
      },
      onError: noop,
    },
  );

  const onSubmit = (data: FormData) => {
    void handleSubmit(data);
  };

  return (
    <OnboardingWrapper
      title={t(`${localeScope}.title`)}
      isNarrow
      withCircleBranding={isEmpty(currentCommunity)}
      shouldShowPoweredByCircle={shouldShowPoweredByCircle}
      isSignUpOrLogIn
    >
      <PageTitle title={t(`${localeScope}.page_title`)} />

      <Form defaultValues={defaultFormState} onSubmit={onSubmit}>
        <Form.Item
          label={t(`${localeScope}.form.email`)}
          name="user[email]"
          hideDescription
          isFirstChild
          hideBorder
          rules={{
            required: t(`${localeScope}.form.errors.email_required`),
            pattern: {
              value: EMAIL_REGEX,
              message: t(`${localeScope}.form.errors.email_invalid`),
            },
          }}
        >
          <Form.Input autoFocus data-testid="forgot_email" />
        </Form.Item>

        <div className="mt-6">
          <Button
            type="submit"
            name="commit"
            variant={currentCommunity ? "primary" : onboardingButtonVariant}
            disabled={isLoading}
            extraLarge
            full
          >
            {t(`${localeScope}.form.submit`)}
          </Button>
        </div>
      </Form>

      <div className="mt-8 flex flex-col items-center justify-center">
        <SignupLink />
        <div className="mt-2">
          <AlreadyHaveAccountLink
            url={authenticationAppRoutes.signIn({ params: urlParams })}
          />
        </div>
      </div>
    </OnboardingWrapper>
  );
};

export default ForgotPasswordPage;
